/**
 * Puts each `$$` of a multi-line display math block on its own line, so
 * remark-math parses the block the model meant.
 *
 * remark-math treats a line-leading `$$` like a code fence: the rest of that
 * line is "meta" and is DISCARDED, and the block closes only on a line that is
 * `$$` alone. Models routinely write
 *
 *   $$\mathbf{\Sigma} = \begin{bmatrix}
 *   ...
 *   \end{bmatrix}$$
 *
 * which loses the matrix head (KaTeX then fails on the first `&`) and never
 * closes, so every heading, figure and paragraph after it became one red
 * KaTeX error. This rewrites that shape to
 *
 *   $$
 *   \mathbf{\Sigma} = \begin{bmatrix}
 *   ...
 *   \end{bmatrix}
 *   $$
 *
 * One-line `$$x$$` and inline `$x$` already parse and are left alone, as is
 * anything inside fenced or inline code (the same spans the citation rewrite
 * skips). Output is a fixed point: running it twice changes nothing.
 *
 * Out of scope: a block inside a blockquote or list item (`> $$...`), whose
 * line prefix this walker does not strip.
 */

import { PROTECTED_SPAN_RE } from './chatCitations';

/** A `$$` at the start of a line, with the up-to-3-space indent a fence allows. */
const OPENER_RE = /^( {0,3})\$\$(.*)$/;
/** A line inside an open block that carries its closing `$$` at the end. */
const TRAILING_CLOSER_RE = /^(.*\S)[ \t]*\$\$[ \t]*$/;

export function normalizeDisplayMathDelimiters(text: string): string {
  if (!text.includes('$$')) return text;

  const protectedSpans = Array.from(text.matchAll(PROTECTED_SPAN_RE), (m) => [
    m.index,
    m.index + m[0].length,
  ]);
  const isProtected = (offset: number) =>
    protectedSpans.some(([start, end]) => offset >= start && offset < end);

  const out: string[] = [];
  let openIndent: string | null = null;
  let offset = 0;
  for (const line of text.split('\n')) {
    const lineStart = offset;
    offset += line.length + 1;
    if (isProtected(lineStart)) {
      out.push(line);
      continue;
    }

    if (openIndent === null) {
      const opener = OPENER_RE.exec(line);
      const rest = opener?.[2] ?? '';
      // Left alone when the rest holds any `$`: a one-line `$$x$$` or a
      // typo'd `$$x$` is refused by remark-math (its meta may not contain
      // `$`), and a `$$$` fence is a block remark-math already parses.
      if (!opener || rest.includes('$')) {
        out.push(line);
        continue;
      }
      openIndent = opener[1];
      if (rest.trim() === '') out.push(line);
      else out.push(`${openIndent}$$`, `${openIndent}${rest.trimStart()}`);
      continue;
    }

    if (line.trim() === '$$') {
      openIndent = null;
      out.push(line);
      continue;
    }
    const closer = TRAILING_CLOSER_RE.exec(line);
    if (closer) {
      out.push(closer[1], `${openIndent}$$`);
      openIndent = null;
      continue;
    }
    out.push(line);
  }
  return out.join('\n');
}
